import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Lock, Shield, Key, Server, Eye, CheckCircle, Menu, X } from 'lucide-react';
import './InfoPage.css';

const SecurityPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const securityFeatures = [
    {
      icon: Lock,
      title: 'Data Encryption',
      description: 'AES-256 encryption at rest and TLS 1.3 in transit to protect all your data'
    },
    {
      icon: Key,
      title: 'Multi-Factor Authentication',
      description: 'Optional MFA for an extra layer of account security'
    },
    {
      icon: Server,
      title: 'SOC 2 Compliant Infrastructure',
      description: 'Data centers meet the highest security and compliance standards'
    },
    {
      icon: Eye,
      title: '24/7 Security Monitoring',
      description: 'Continuous monitoring and intrusion detection systems'
    },
    {
      icon: Shield,
      title: 'Regular Security Audits',
      description: 'Penetration testing and third-party security assessments'
    },
    {
      icon: CheckCircle,
      title: 'Automatic Backups',
      description: 'Daily automated backups with disaster recovery procedures'
    }
  ];

  return (
    <div className="info-page">
      <motion.nav initial={{ y: -100 }} animate={{ y: 0 }} className="info-nav">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => navigate('/')}>
            <img src="/homequest-logo.svg" alt="HomeQuest Tech" className="logo-image" />
            <div className="logo-tagline">Where Land Meets Logic</div>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <a onClick={() => navigate('/')}>Home</a>
            <a onClick={() => navigate('/features')}>Features</a>
            <a onClick={() => navigate('/pricing')}>Pricing</a>
            <button onClick={() => navigate('/login')} className="nav-login">Sign In</button>
            <button onClick={() => navigate('/pricing')} className="nav-cta">Start Free Trial</button>
          </div>
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </motion.nav>

      <section className="info-hero">
        <div className="hero-container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="hero-badge">
              <Shield className="badge-icon" />
              <span>SECURITY</span>
            </div>
            <h1 className="hero-title">
              Enterprise-Grade <span className="gradient-text">Security</span>
            </h1>
            <p className="hero-subtitle">
              Your construction data is protected by bank-level security measures
            </p>
          </motion.div>
        </div>
      </section>

      <section className="info-content">
        <div className="content-container">
          <h2>Our Security Commitment</h2>
          <p>
            At HomeQuest Tech, security is not an afterthought—it's built into every layer of our platform. We understand that your CAD files, project data, and communications are critical business assets that must be protected.
          </p>

          <div style={{ marginTop: '3rem' }}>
            <h2>Security Features</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginTop: '2rem' }}>
              {securityFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  style={{
                    padding: '2rem',
                    background: 'rgba(31, 41, 55, 0.6)',
                    borderRadius: '12px',
                    border: '1px solid rgba(59, 130, 246, 0.2)'
                  }}
                >
                  <feature.icon style={{ width: '40px', height: '40px', color: '#3B82F6', marginBottom: '1rem' }} />
                  <h3 style={{ color: '#F9FAFB', marginBottom: '0.5rem' }}>{feature.title}</h3>
                  <p style={{ color: '#D1D5DB', margin: 0 }}>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <h2>Data Encryption</h2>
          <h3>At Rest</h3>
          <p>
            All data stored in our systems is encrypted using AES-256 encryption, the same standard used by financial institutions and government agencies. This includes:
          </p>
          <ul>
            <li>CAD files and floor plans</li>
            <li>Project data and selections</li>
            <li>Communications and messages</li>
            <li>Vendor information and bids</li>
            <li>Invoices and financial data</li>
            <li>Site photos and documentation</li>
          </ul>

          <h3>In Transit</h3>
          <p>
            All data transmitted between your browser and our servers is protected by TLS 1.3, the latest and most secure transport layer security protocol. This ensures that your data cannot be intercepted or tampered with during transmission.
          </p>

          <h2>Infrastructure Security</h2>
          <h3>SOC 2 Compliance</h3>
          <p>
            Our infrastructure is hosted in SOC 2 Type II certified data centers that meet rigorous security, availability, and confidentiality standards. These facilities feature:
          </p>
          <ul>
            <li>Physical security with biometric access controls</li>
            <li>24/7 on-site security personnel</li>
            <li>Video surveillance and monitoring</li>
            <li>Redundant power and cooling systems</li>
            <li>Fire suppression and environmental controls</li>
          </ul>

          <h3>Network Security</h3>
          <ul>
            <li><strong>Firewalls:</strong> Multi-layered firewall protection to prevent unauthorized access</li>
            <li><strong>DDoS Protection:</strong> Advanced DDoS mitigation to ensure service availability</li>
            <li><strong>Intrusion Detection:</strong> Real-time monitoring for suspicious activity</li>
            <li><strong>Network Segmentation:</strong> Isolated networks for different system components</li>
          </ul>

          <h2>Application Security</h2>
          <h3>Secure Authentication</h3>
          <ul>
            <li>Password hashing using bcrypt with per-user salts</li>
            <li>Optional multi-factor authentication (MFA) for enhanced security</li>
            <li>Session management with automatic timeout</li>
            <li>Protection against brute-force attacks</li>
          </ul>

          <h3>Access Controls</h3>
          <ul>
            <li>Role-based access control (RBAC) for team members</li>
            <li>Granular permissions for different project roles</li>
            <li>Audit logs tracking all user actions</li>
            <li>Ability to revoke access instantly when needed</li>
          </ul>

          <h3>Code Security</h3>
          <ul>
            <li>Regular security code reviews</li>
            <li>Automated vulnerability scanning</li>
            <li>Dependency monitoring for known vulnerabilities</li>
            <li>Secure development lifecycle practices</li>
          </ul>

          <h2>Data Backup and Recovery</h2>
          <p>
            Your data is automatically backed up daily to geographically distributed locations. In the event of a disaster:
          </p>
          <ul>
            <li>Automated backups retained for 30 days</li>
            <li>Geographic redundancy across multiple regions</li>
            <li>Tested disaster recovery procedures</li>
            <li>Recovery Point Objective (RPO) of 24 hours</li>
            <li>Recovery Time Objective (RTO) of 4 hours</li>
          </ul>

          <h2>Vendor Security</h2>
          <p>
            When you share project information with vendors through our platform:
          </p>
          <ul>
            <li>Vendors only see information you explicitly share</li>
            <li>Secure links with expiration dates for bid requests</li>
            <li>All vendor communications are encrypted</li>
            <li>Audit trail of all vendor access</li>
          </ul>

          <h2>Payment Security</h2>
          <p>
            We use Stripe, a PCI DSS Level 1 certified payment processor, for all payment transactions. We never store your complete credit card information on our servers. Stripe handles all sensitive payment data with the highest security standards.
          </p>

          <h2>Security Monitoring</h2>
          <p>
            Our security operations center provides:
          </p>
          <ul>
            <li>24/7/365 security monitoring</li>
            <li>Real-time threat detection and response</li>
            <li>Automated security alerts</li>
            <li>Regular security incident drills</li>
            <li>Incident response procedures</li>
          </ul>

          <h2>Third-Party Audits</h2>
          <p>
            We engage independent security firms to conduct:
          </p>
          <ul>
            <li>Annual penetration testing</li>
            <li>Vulnerability assessments</li>
            <li>Security architecture reviews</li>
            <li>Compliance audits</li>
          </ul>

          <h2>Employee Security</h2>
          <ul>
            <li>Background checks for all employees</li>
            <li>Security awareness training</li>
            <li>Principle of least privilege for system access</li>
            <li>Confidentiality agreements</li>
            <li>Regular security training updates</li>
          </ul>

          <h2>Data Privacy</h2>
          <p>
            Security and privacy go hand in hand. We:
          </p>
          <ul>
            <li>Do not sell or share your data with third parties for marketing</li>
            <li>Only use your data to provide the Service</li>
            <li>Allow you to export and delete your data at any time</li>
            <li>Comply with GDPR, CCPA, and other privacy regulations</li>
            <li>Maintain detailed privacy policies (see our <a onClick={() => navigate('/privacy')} style={{ color: '#3B82F6', cursor: 'pointer' }}>Privacy Policy</a>)</li>
          </ul>

          <h2>Incident Response</h2>
          <p>
            In the unlikely event of a security incident:
          </p>
          <ul>
            <li>We have a dedicated incident response team</li>
            <li>Incidents are investigated and contained immediately</li>
            <li>Affected customers are notified within 72 hours</li>
            <li>Post-incident analysis to prevent recurrence</li>
            <li>Coordination with law enforcement when appropriate</li>
          </ul>

          <h2>Responsible Disclosure</h2>
          <p>
            If you discover a security vulnerability, please report it to us at support@homequesttech.com. We appreciate responsible disclosure and will work with you to resolve any issues promptly.
          </p>

          <h2>Security Best Practices for Users</h2>
          <p>
            While we provide robust security, you can enhance your account security by:
          </p>
          <ul>
            <li>Using a strong, unique password</li>
            <li>Enabling multi-factor authentication (MFA)</li>
            <li>Not sharing your account credentials</li>
            <li>Logging out when using shared devices</li>
            <li>Reviewing your account activity regularly</li>
            <li>Keeping your contact information up to date</li>
          </ul>

          <h2>Questions About Security?</h2>
          <p>
            If you have questions about our security practices or need more information, please contact us at support@homequesttech.com.
          </p>

          <button onClick={() => navigate('/contact')} className="cta-button">
            Contact Security Team
          </button>
        </div>
      </section>

      <footer className="info-footer">
        <div className="footer-container">
          <div className="footer-content">
            <p>&copy; 2025 HomeQuest Tech. All rights reserved.</p>
            <div className="footer-links">
              <a onClick={() => navigate('/')}>Home</a>
              <a onClick={() => navigate('/features')}>Features</a>
              <a onClick={() => navigate('/privacy')}>Privacy</a>
              <a onClick={() => navigate('/terms')}>Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SecurityPage;
